import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { Loader2, Mic, Send, Plus, Trash2, MessageSquare, History, Sparkles, FileText, MessageCircle, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import AiPageShell from "../../components/ai/AiPageShell";
import { AiMarkdown } from "../../components/ai/AiMarkdown";
import AkilOnayPanel from "../../components/ai/AkilOnayPanel";
import { 
    akilChat, 
    akilDegerKaybiKaydet, 
    akilMerkezKomut,
    akilChatHistoryList,
    akilChatHistoryCreate,
    akilChatHistoryDetail,
    akilChatHistoryDelete
} from "../../lib/akilApi";
import { AKIL_CHAT_GREETING, formatAkilReply } from "../../lib/akilChatUtils";
import { buildContextPrompt, getAkilContext, setSonHesap, getSonHesap } from "../../lib/akilContext";
import { useAkilSpeech } from "../../lib/useAkilSpeech";

export default function AkilChatPage() {
    const [context] = useState(() => getAkilContext());
    const [history, setHistory] = useState([]);
    const [activeChatId, setActiveChatId] = useState(null);
    const [messages, setMessages] = useState([
        {
            role: "ai",
            text: context
                ? `${AKIL_CHAT_GREETING}\n\n${buildContextPrompt(context).trim()}`
                : AKIL_CHAT_GREETING,
        },
    ]);
    const [input, setInput] = useState("");
    const [loading, setLoading] = useState(false);
    const [loadingHistory, setLoadingHistory] = useState(false);
    const [pending, setPending] = useState(null);
    const [sonHesap, setSonHesapState] = useState(() => getSonHesap());
    const scrollRef = useRef(null);

    const quickPrompts = useMemo(
        () => [
            "Cemal adına müvekkil hepsini getir",
            "Ahmet Yılmaz dosyaları",
            "Tahkime geçir",
            "Başvuru yap",
            "Tahkime geçmesi gereken dosyaları göster",
            "Günlük sistem taraması yap",
            "Bugünkü ödemeleri özetle",
            "Değer kaybı hesapla 2019 Megane 120000 km rayiç 650000 hasar 42000",
        ],
        [],
    );

    const { listening, start: startSpeech } = useAkilSpeech((text) => {
        setInput((prev) => (prev ? `${prev} ${text}` : text));
    });

    const loadHistory = useCallback(async () => {
        setLoadingHistory(true);
        try {
            const res = await akilChatHistoryList();
            if (res.success) {
                setHistory(res.results || []);
            }
        } catch (e) {
            console.error("Sohbet geçmişi yüklenemedi:", e);
        } finally {
            setLoadingHistory(false);
        }
    }, []);

    useEffect(() => {
        loadHistory();
    }, [loadHistory]);

    useEffect(() => {
        setTimeout(() => scrollRef.current?.scrollTo({ top: 99999, behavior: "smooth" }), 50);
    }, [messages, loading]);

    const selectChat = async (id) => {
        if (!id) {
            setActiveChatId(null);
            setMessages([
                {
                    role: "ai",
                    text: context
                        ? `${AKIL_CHAT_GREETING}\n\n${buildContextPrompt(context).trim()}`
                        : AKIL_CHAT_GREETING,
                },
            ]);
            return;
        }

        setLoading(true);
        try {
            const res = await akilChatHistoryDetail(id);
            if (res.success) {
                setActiveChatId(id);
                const loadedMsgs = (res.mesajlar || []).map((m) => ({
                    role: m.yon === "GELEN" ? "user" : "ai",
                    text: m.mesaj,
                    payload: m.payload,
                }));
                if (loadedMsgs.length === 0) {
                    setMessages([
                        {
                            role: "ai",
                            text: context
                                ? `${AKIL_CHAT_GREETING}\n\n${buildContextPrompt(context).trim()}`
                                : AKIL_CHAT_GREETING,
                        },
                    ]);
                } else {
                    setMessages(loadedMsgs);
                }
            }
        } catch (e) {
            toast.error("Sohbet detayları yüklenemedi.");
        } finally {
            setLoading(false);
        }
    };

    const createNewChat = async () => {
        try {
            const res = await akilChatHistoryCreate("Yeni Sohbet");
            if (res.success) {
                toast.success("Yeni sohbet oluşturuldu.");
                await loadHistory();
                selectChat(res.id);
            }
        } catch (e) {
            toast.error("Yeni sohbet oluşturulamadı.");
        }
    };

    const deleteChat = async (id, e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!window.confirm("Bu sohbeti silmek istediğinize emin misiniz?")) return;

        try {
            const res = await akilChatHistoryDelete(id);
            if (res.success) {
                toast.success("Sohbet silindi.");
                if (activeChatId === id) {
                    selectChat(null);
                }
                loadHistory();
            }
        } catch (e) {
            toast.error("Sohbet silinemedi.");
        }
    };

    const send = async (text, opts = {}) => {
        const raw = (text || input).trim();
        if (!raw && !opts.parsed) return;
        const mesaj = opts.skipContext ? raw : buildContextPrompt(context) + raw;
        setLoading(true);
        if (raw && !opts.silentUser) {
            setMessages((m) => [...m, { role: "user", text: raw }]);
            setInput("");
        }
        try {
            const ctx = getAkilContext() || {};
            if (sonHesap) ctx.son_hesap = sonHesap;

            let data;
            if (raw.startsWith("/") && !opts.parsed) {
                data = await akilMerkezKomut({ komut: raw, payload: ctx });
                if (!data.mesaj && !data.yanit_metni) {
                    data = {
                        success: data.success !== false,
                        intent: "merkez_komut",
                        mesaj: data.metin || JSON.stringify(data, null, 2).slice(0, 4000),
                        son_hesap: data.gercek_deger_kaybi ? data : data.hesap,
                    };
                }
            } else {
                const payload = opts.parsed
                    ? { parsed: opts.parsed, onay: !!opts.onay, mesaj: mesaj || "onay", context: ctx }
                    : { mesaj, context: ctx };
                
                // Pass sohbet_id to backend so it gets saved
                payload.sohbet_id = activeChatId || "new";
                data = await akilChat(payload);
            }

            if (data.onay_gerekli && !opts.onay) {
                setPending({
                    parsed: data.parsed,
                    aciklama: data.aciklama || data.mesaj,
                    onay_ozeti: data.onay_ozeti,
                });
                setMessages((m) => [
                    ...m,
                    {
                        role: "ai",
                        text: data.onay_ozeti || data.aciklama || data.mesaj || "Onay gerekli.",
                    },
                ]);
                return;
            }

            setPending(null);
            if (data.son_hesap || data.kaydet_action?.hesap) {
                const h = data.son_hesap || data.kaydet_action.hesap;
                setSonHesap(h);
                setSonHesapState(h);
            }

            // If a new chat was created implicitly by backend, update active id and reload history
            if (data.sohbet_id && !activeChatId) {
                setActiveChatId(data.sohbet_id);
                loadHistory();
            } else if (activeChatId) {
                // Just reload history to get updated titles
                loadHistory();
            }

            setMessages((m) => [...m, { role: "ai", text: formatAkilReply(data), payload: data }]);
        } catch (err) {
            const msg =
                err.response?.data?.error ||
                err.response?.data?.detail ||
                err.response?.data?.mesaj ||
                (err.code === "ECONNABORTED" ? "İstek zaman aşımına uğradı (AI işlemi uzun sürdü)." : null) ||
                err.message ||
                "İstek başarısız.";
            toast.error(String(msg));
            setMessages((m) => [...m, { role: "ai", text: `Hata: ${msg}` }]);
        } finally {
            setLoading(false);
        }
    };

    // Contextual smart suggestions based on the last AI message payload
    const smartSuggestions = useMemo(() => {
        const lastMsg = messages[messages.length - 1];
        if (!lastMsg || lastMsg.role !== "ai" || !lastMsg.payload) return [];

        const payload = lastMsg.payload;
        const suggestions = [];

        if (payload.intent === "deger_kaybi_hesapla" || payload.son_hesap) {
            suggestions.push({
                label: "Bu Dosya İçin Dilekçe Oluştur",
                icon: FileText,
                action: () => send("Bu dosya için değer kaybı dilekçesi oluştur"),
            });
        }

        if (payload.muvekkil || payload.sigorta_dosyasi) {
            suggestions.push({
                label: "Müvekkile WhatsApp Mesajı Taslakla",
                icon: MessageCircle,
                action: () => send("Müvekkil için WhatsApp bilgilendirme mesajı taslakla"),
            });
        }

        return suggestions;
    }, [messages]);

    return (
        <AiPageShell
            title="Akıl Yapay Zeka Asistanı"
            subtitle="İsim/TC arama, müvekkil+dosya paketi, tahkime geçir / başvuru yap (dosya sayfasındayken bağlam otomatik)."
        >
            <div className="rounded-2xl border border-white/10 bg-[#121A2F]/80 overflow-hidden flex h-[calc(100vh-220px)] min-h-[480px]">
                {/* Left Sidebar - Chat History */}
                <div className="w-64 border-r border-white/10 flex flex-col bg-[#0B0F19]/60 shrink-0 hidden md:flex">
                    <div className="p-4 border-b border-white/10 flex items-center justify-between">
                        <div className="flex items-center gap-2 text-xs font-semibold text-slate-400 uppercase tracking-wider">
                            <History className="h-4 w-4 text-[#D4AF37]" />
                            Sohbet Geçmişi
                        </div>
                        <button
                            onClick={createNewChat}
                            className="p-1.5 rounded-lg bg-[#D4AF37]/10 hover:bg-[#D4AF37]/20 text-[#D4AF37] transition"
                            title="Yeni Sohbet Başlat"
                        >
                            <Plus className="h-4 w-4" />
                        </button>
                    </div>

                    <div className="flex-1 overflow-y-auto p-2 space-y-1">
                        {loadingHistory ? (
                            <div className="flex items-center justify-center py-8 text-slate-500 text-xs gap-2">
                                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Yükleniyor...
                            </div>
                        ) : history.length === 0 ? (
                            <div className="text-center py-8 text-slate-500 text-xs">
                                Henüz sohbet geçmişi yok.
                            </div>
                        ) : (
                            history.map((chat) => (
                                <div
                                    key={chat.id}
                                    onClick={() => selectChat(chat.id)}
                                    className={`group flex items-center justify-between px-3 py-2.5 rounded-xl cursor-pointer transition text-xs ${
                                        activeChatId === chat.id
                                            ? "bg-[#D4AF37]/10 text-[#D4AF37] border border-[#D4AF37]/20"
                                            : "text-slate-400 hover:bg-white/5 hover:text-slate-200"
                                    }`}
                                >
                                    <div className="flex items-center gap-2 min-w-0 flex-1">
                                        <MessageSquare className="h-3.5 w-3.5 shrink-0 opacity-60" />
                                        <span className="truncate font-medium">{chat.baslik}</span>
                                    </div>
                                    <button
                                        onClick={(e) => deleteChat(chat.id, e)}
                                        className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-rose-500/20 text-slate-500 hover:text-rose-400 transition"
                                        title="Sohbeti Sil"
                                    >
                                        <Trash2 className="h-3 w-3" />
                                    </button>
                                </div>
                            ))
                        )}
                    </div>
                </div>

                {/* Right Area - Chat Window */}
                <div className="flex-1 flex flex-col min-w-0 bg-transparent">
                    <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 lg:p-6 space-y-4">
                        {messages.map((msg, i) => (
                            <div
                                key={i}
                                className={`max-w-[92%] rounded-2xl px-4 py-3 ${
                                    msg.role === "user"
                                        ? "ml-auto bg-[#D4AF37]/20 border border-[#D4AF37]/30 text-[#F8FAFC] text-sm"
                                        : "mr-auto bg-white/[0.04] border border-white/10 text-slate-200"
                                }`}
                            >
                                {typeof msg.text === "string" ? (
                                    <AiMarkdown text={msg.text} />
                                ) : (
                                    msg.text
                                )}
                            </div>
                        ))}
                        {loading ? (
                            <div className="flex items-center gap-2 text-slate-400 text-sm">
                                <Loader2 className="h-4 w-4 animate-spin" /> Akıl çalışıyor…
                            </div>
                        ) : null}
                    </div>

                    {/* Contextual smart suggestions */}
                    {smartSuggestions.length > 0 && !loading && (
                        <div className="mx-4 mb-2 flex flex-wrap gap-2 p-2 rounded-xl bg-[#D4AF37]/5 border border-[#D4AF37]/10">
                            <div className="flex items-center gap-1 text-[11px] font-semibold text-[#D4AF37] uppercase tracking-wider w-full mb-1">
                                <Sparkles className="h-3 w-3" />
                                Akıllı Öneriler
                            </div>
                            {smartSuggestions.map((sug, idx) => {
                                const Icon = sug.icon;
                                return (
                                    <button
                                        key={idx}
                                        onClick={sug.action}
                                        className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-[#D4AF37]/10 hover:bg-[#D4AF37]/20 text-[#D4AF37] border border-[#D4AF37]/20 transition font-medium"
                                    >
                                        <Icon className="h-3.5 w-3.5" />
                                        {sug.label}
                                    </button>
                                );
                            })}
                        </div>
                    )}

                    {sonHesap?.gercek_deger_kaybi ? (
                        <div className="mx-4 mb-2 flex flex-wrap items-center gap-2 rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-4 py-2 text-xs">
                            <span className="text-emerald-200">
                                Son DK: ₺{Number(sonHesap.gercek_deger_kaybi).toLocaleString("tr-TR")}
                            </span>
                            <button
                                type="button"
                                className="px-2 py-1 rounded bg-[#D4AF37] text-black"
                                onClick={async () => {
                                    try {
                                        await akilDegerKaybiKaydet({
                                            ...sonHesap,
                                            dosya_no: sonHesap.dosya_no || context?.dosya_no,
                                            sigorta_dosyasi_id: sonHesap.dosya_id || context?.sigorta_dosyasi_id,
                                        });
                                        toast.success("Dosyaya kaydedildi");
                                    } catch (e) {
                                        toast.error(e.response?.data?.error || e.message);
                                    }
                                }}
                            >
                                Dosyaya kaydet
                            </button>
                        </div>
                    ) : null}

                    {pending ? (
                        <div className="mx-4 mb-2">
                            <AkilOnayPanel
                                ozet={pending.onay_ozeti || pending.aciklama}
                                onConfirm={() =>
                                    send("", {
                                        parsed: pending.parsed,
                                        onay: true,
                                        silentUser: true,
                                        skipContext: true,
                                    })
                                }
                                onCancel={() => setPending(null)}
                                loading={loading}
                            />
                        </div>
                    ) : null}

                    <div className="border-t border-white/10 p-3 lg:p-4 space-y-3">
                        <div className="flex flex-wrap gap-2">
                            {quickPrompts.map((q) => (
                                <button
                                    key={q}
                                    type="button"
                                    onClick={() => send(q)}
                                    className="text-[11px] px-2.5 py-1 rounded-full border border-white/10 text-slate-400 hover:text-white hover:border-[#D4AF37]/40 transition"
                                >
                                    {q}
                                </button>
                            ))}
                        </div>
                        <form
                            className="flex gap-2"
                            onSubmit={(e) => {
                                e.preventDefault();
                                send();
                            }}
                        >
                            <button
                                type="button"
                                onClick={startSpeech}
                                disabled={loading || listening}
                                className={`h-12 w-12 rounded-xl border flex items-center justify-center shrink-0 ${
                                    listening
                                        ? "border-red-400/50 text-red-300 animate-pulse"
                                        : "border-white/10 text-slate-400 hover:text-[#D4AF37]"
                                }`}
                                title="Sesli komut"
                            >
                                <Mic className="h-5 w-5" />
                            </button>
                            <input
                                value={input}
                                onChange={(e) => setInput(e.target.value)}
                                placeholder={
                                    context
                                        ? "Bu dosya hakkında sorun…"
                                        : "Ad/TC, plaka veya MBS no ile arayın…"
                                }
                                className="flex-1 rounded-xl bg-[#0B0F19] border border-white/10 px-4 py-3 text-sm outline-none focus:border-[#D4AF37]/50"
                            />
                            <button
                                type="submit"
                                disabled={loading || !input.trim()}
                                className="h-12 w-12 rounded-xl bg-[#D4AF37] text-black flex items-center justify-center disabled:opacity-40"
                            >
                                <Send className="h-5 w-5" />
                            </button>
                        </form>
                    </div>
                </div>
            </div>
        </AiPageShell>
    );
}
